import { MapObjectReference } from '../../core/map-object';
import { Router } from '@angular/router';
import { SELECTION_WORKSPACE_ROUTE, workspaceNavigationInfo } from '../../../services/workspace-route';
import { isSelectableFeature } from '../../core/selectable-feature';
import { CacheService } from '../../../services/cache.service';
import { DensityFeature } from '../../features/density/density-feature';
import {
  Component,
  AfterViewInit,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, Subscription, firstValueFrom, auditTime, tap } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MapNavigationService } from '../../core/map-navigation.service';
import { MapFeatureManagerService } from '../../core/map-feature-manager.service';
import { ProspectFeature } from '../../features/prospects/prospect-feature';
import { ZipCodeAreaFeature } from '../../features/zipcode-areas/zipcode-area-feature';
import { TerritoryFeature } from '../../features/territories/territory-feature';
import { SalesRouteFeature } from '../../features/sales-routes/sales-route-feature';
import {
  MapSelectionContextService, FeatureSelectionSummary,
} from '../../core/map-selection-context.service';
import { AppContextService } from '../../../services/app-context.service';
import { CustomerFeature } from '../../features/customers/customer-feature';
import { LeadFeature } from '../../features/leads/lead-feature';
import { MapInteractionController } from './map-interaction-controller';
import {
  MapBounds,
} from '../../core/spatial/map-viewport';
import { createBaseTileLayer } from '../../core/base-map';
import * as L from 'leaflet';

const MAX_AGENT_OBJECT_IDS = 500;

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['../map-control.css', './map.component.css']
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
  mapHintsVisible = false;

  private map!: L.Map;
  /**
   * Watches the map's own box so Leaflet is told when it changes size.
   *
   * Leaflet caches the container's pixel dimensions at init and recomputes them
   * only on window resize or an explicit invalidateSize(). Observing the
   * element catches shell and viewport changes in one place, including a future
   * shared Workspace dock, without coupling the map to layout state.
   */
  private resizeObserver?: ResizeObserver;
  private zoom: number = 15;
  // Defensive teardown for the subscriptions and the Leaflet map. app-map is
  // mounted persistently (app.component.html), so it is not normally destroyed;
  // but if it ever is (HMR, a future structural change) an uncleaned instance
  // would keep reacting to viewport events and re-hitting /place/clusters.
  // `destroyed` guards the async initAll() callback below from
  // running on a torn-down map.
  private destroy$ = new Subject<void>();
  private destroyed = false;
  // Pans may emit several moveend events in quick succession, so only that
  // gesture is debounced. Zooms publish immediately after all zoomend handlers;
  // delaying both made every cluster transition wait an unnecessary 200 ms.
  private panEnd$ = new Subject<void>();
  private skipMoveEndAfterZoom = false;
  // The map starts on a harmless fallback center while tenant bounds load.
  // Do not publish that temporary box to viewport-backed features: their first
  // request must use either the tenant's Place bounds or the final fallback.
  private viewportReady = false;
  private static readonly PAN_DEBOUNCE_MS = 200;
  latitude: number = 40.7128;
  longitude: number = -74.0060;

  private agentContextRegistration?: Subscription;
  private viewContextRegistration?: Subscription;

  constructor(
    private http: HttpClient,
    private mapNavigation: MapNavigationService,
    private featureManager: MapFeatureManagerService,
    private prospectFeature: ProspectFeature,
    public readonly density: DensityFeature,
    private customers: CustomerFeature,
    private leads: LeadFeature,
    private territories: TerritoryFeature,
    private salesRoute: SalesRouteFeature,
    private selectionContext: MapSelectionContextService,
    private agentContext: AppContextService,
    public readonly interactionController: MapInteractionController,
    private zone: NgZone,
    private cache: CacheService,
    private zipCodeAreas: ZipCodeAreaFeature,
    @Optional() private readonly router?: Router,
  ) { }

  ngOnInit(): void {
    // Feature registration is application state, not Leaflet DOM setup. Keep it
    // before the first view check so sidebar and layer-menu subscribers receive
    // one stable initial registry instead of changing during ngAfterViewInit.
    this.featureManager.registerFeature(this.customers);
    this.featureManager.registerFeature(this.leads);
    this.featureManager.registerFeature(this.density);
    this.featureManager.registerFeature(this.prospectFeature);
    this.featureManager.registerFeature(this.territories);
    this.featureManager.registerFeature(this.zipCodeAreas);
    this.featureManager.registerFeature(this.salesRoute);

    // Selection owns identity. Navigation supplies presentation and camera only.
    this.mapNavigation.focusRequested$.pipe(takeUntil(this.destroy$)).subscribe(reference => {
      const feature = this.featureManager.getFeature(reference.featureKey);
      if (!isSelectableFeature(feature)) return;
      const sole = this.currentSelections.length === 1
        && this.currentSelections[0].count === 1
        && this.currentSelections[0].feature === feature
        && this.currentSelections[0].objectIds[0] === reference.objectId;
      if (!sole) {
        this.selectionContext.clearAll();
        feature.selectObjectById?.(reference.objectId);
      }
      const automatic = this.automaticInspection?.featureKey === reference.featureKey
        && this.automaticInspection.objectId === reference.objectId;
      this.automaticInspection = undefined;
      if (!this.inspectingSelection && !automatic) this.openSelectionWorkspace();
    });
    this.selectionContext.selections$.pipe(tap(selections => this.currentSelections = selections), auditTime(0), takeUntil(this.destroy$)).subscribe(selections => {
      const single = selections.length === 1 && selections[0].count === 1
        && selections[0].objectIds.length === 1 ? selections[0] : undefined;
      if (!single) {
        this.automaticInspection = undefined;
        this.mapNavigation.clearFocus();
      } else if (!this.mapNavigation.hasObjectFocus(single.feature.key, single.objectIds[0])) {
        this.mapNavigation.clearFocus();
        this.automaticInspection = { featureKey: single.feature.key, objectId: single.objectIds[0] };
        this.inspectingSelection = true;
        try { single.feature.inspectObject?.(single.objectIds[0]); }
        finally { this.inspectingSelection = false; }
      }
    });
    this.interactionController.selectionApplied$.pipe(auditTime(0), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.currentSelections.length) this.openSelectionWorkspace();
      });

    this.registerMapContext();
    this.cache.sessionChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => this.registerMapContext());
  }

  private registerMapContext(): void {
    this.registerMapView();
    this.agentContextRegistration?.unsubscribe();
    this.agentContextRegistration = this.agentContext.register({
      key: 'map-object-selection',
      name: 'Map object selection',
      state$: this.selectionContext.selections$,
      describe: selections => selections.map(({ feature, count, objectIds }) => {
        const included = objectIds.slice(0, MAX_AGENT_OBJECT_IDS);
        const unresolved = count > objectIds.length
          ? ' Some objects are represented by unresolved clusters; their IDs are not included.' : '';
        return `${feature.name} [${feature.key}]: ${count} selected. `
          + `Business object IDs (${included.length} of ${count}): ${JSON.stringify(included)}.`
          + unresolved;
      }).join('\n'),
    });
  }

  private registerMapView(): void {
    this.viewContextRegistration?.unsubscribe();
    this.viewContextRegistration = this.agentContext.registerView({
      key: 'map-view', name: 'Map view', priority: -40,
      read: () => ({ camera: this.mapNavigation.captureView(),
        selection: this.selectionContext.capture() }),
      describe: state => JSON.stringify({ camera: state.camera, focusedObject: this.mapNavigation.focusedObject,
        layers: this.featureManager.getFeatures().map(feature => ({ key: feature.key, name: feature.name,
          visible: feature.enabled, loading: feature.loading ?? false, total: feature.count ?? null,
          ...feature.contextState })) }),
      restore: state => {
        this.mapNavigation.restoreView(state.camera);
        this.selectionContext.restore(state.selection);
      },
    });
  }

  private currentSelections: readonly FeatureSelectionSummary[] = [];
  private openingSelection = false;
  private inspectingSelection = false;
  private automaticInspection?: MapObjectReference;

  private openSelectionWorkspace(): void {
    if (!this.router || this.openingSelection || this.router.url.split('?')[0] === SELECTION_WORKSPACE_ROUTE
      || this.selectionContext.presentedByPage) return;
    this.openingSelection = true;
    // Route navigation preserves the existing unsaved-change guard on editors.
    void this.zone.run(() => this.router!.navigateByUrl(SELECTION_WORKSPACE_ROUTE, {
      info: workspaceNavigationInfo('docked-right'),
    })).finally(() => this.openingSelection = false);
  }

  ngAfterViewInit(): void {
    // Leaflet owns high-frequency pointer, wheel and animation events. Create
    // it outside Angular so those events do not check the complete application
    // tree; settled viewport and selection outputs explicitly re-enter below.
    this.map = this.zone.runOutsideAngular(() =>
      L.map('userMap', { zoomControl: false, doubleClickZoom: false })
        .setView([this.latitude, this.longitude], this.zoom));
    this.mapNavigation.attach(this.map);
    this.watchOwnSize();

    // Keep the product signature alongside the basemap attribution.
    //
    // The year sits outside the anchor: what is clickable is the company, and
    // a link whose text includes a date reads as though the date were part of
    // the destination.
    this.map.attributionControl.setPrefix(
      '©2026 <a href="https://optixway.com" target="_blank" rel="noopener">OptixWay</a>'
    );

    createBaseTileLayer().addTo(this.map);

    this.mapNavigation.viewport$
      .pipe(takeUntil(this.destroy$))
      .subscribe(viewport => this.featureManager.updateViewport(viewport));

    // Viewport-scoped data layers refresh after the map settles. Lead data is
    // a complete business filter; Customer data uses this viewport boundary.
    this.panEnd$.pipe(
      debounceTime(MapComponent.PAN_DEBOUNCE_MS),
      takeUntil(this.destroy$),
    ).subscribe(() => this.publishViewport());

    this.map.on('zoomend', () => {
      this.skipMoveEndAfterZoom = true;
      // MarkerClusterGroup also handles zoomend. Publish in a microtask so
      // every cluster layer enters its animation before a cached viewport
      // response can replace the markers participating in that animation.
      queueMicrotask(() => {
        if (!this.destroyed && this.viewportReady) {
          this.zone.run(() => this.publishViewport());
        }
      });
    });
    this.map.on('moveend', () => {
      if (this.skipMoveEndAfterZoom) {
        this.skipMoveEndAfterZoom = false;
        return;
      }
      if (this.viewportReady) this.zone.run(() => this.panEnd$.next());
    });

    // Boundaries under points, so a marker is never swallowed by a polygon.
    this.map.addLayer(this.density.layer);
    this.density.attach(this.map);
    this.map.addLayer(this.zipCodeAreas.layer);
    this.zipCodeAreas.attach(this.map);
    this.map.addLayer(this.territories.layer);
    this.map.addLayer(this.salesRoute.layer);
    this.map.addLayer(this.prospectFeature.layer);
    this.map.addLayer(this.customers.layer);
    this.map.addLayer(this.leads.layer);
    this.customers.useMap(this.map);
    this.leads.useMap(this.map);

    // Labels re-decide what fits and what collides whenever the view
    // settles, which the layer cannot see without the map.
    this.territories.useMap(this.map, () => Array.from(
      // What the shell floats over the canvas. The layer keeps its names out
      // from under them, and only the shell knows they are there.
      document.querySelectorAll('.ws-restore-chip, .copilot-chip')));
    // Area-prospecting grid overlay (selection canvas + cell painting). Lives in
    // the Prospect feature's layer group so the layer's eye shows/hides it — and
    // everything it draws, selection included — like any other feature.
    this.prospectFeature.attach(this.map);

    // The single gesture pipeline. It owns map.dragging and selection input and
    // dispatches to visible features by their selection or popover capability.
    this.interactionController.attach(this.map);

    // Pass the whole registry: inspection does not require selection support.
    this.featureManager.features$
      .pipe(takeUntil(this.destroy$))
      .subscribe(features => {
        this.interactionController.setFeatures(features);
      });

    // Initialize and render separately
    void this.initializeFeaturesAndViewport();
  }

  private async initializeFeaturesAndViewport(): Promise<void> {
    const version = this.cache.sessionVersion;
    const [, bounds] = await Promise.all([
      this.featureManager.initAll(),
      this.loadPlaceBounds(),
    ]);
    if (this.destroyed || version !== this.cache.sessionVersion) return;

    this.featureManager.renderAll();
    if (bounds) {
      this.mapNavigation.fitBounds(
        [[bounds.south, bounds.west], [bounds.north, bounds.east]],
        { maxZoom: 15 },
      );
    }

    // fitBounds emits zoom/move events synchronously. They remain gated until
    // the tenant bounds have been applied, then this publishes exactly one
    // initial viewport. An empty tenant intentionally keeps the NYC fallback.
    this.viewportReady = true;
    this.publishViewport();
  }

  private publishViewport(): void {
    this.mapNavigation.publishViewport();
  }

  private async loadPlaceBounds(): Promise<MapBounds | null> {
    try {
      return await firstValueFrom(
        this.cache.track(() => this.http.get<MapBounds>('/api/place/bounds')),
        { defaultValue: null },
      );
    } catch (error) {
      // Bounds are framing metadata, not a prerequisite for using the map.
      // Keep the navigator's fallback center and let the viewport load proceed.
      console.error('Failed to load Place map bounds', error);
      return null;
    }
  }

  ngOnDestroy(): void {
    this.density.detach();
    // Stop both subscriptions and fully remove the Leaflet map so its DOM and
    // event handlers (including moveend) go with it — standard
    // cleanup that keeps a torn-down instance from lingering in shared state.
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.agentContextRegistration?.unsubscribe();
    this.agentContextRegistration = undefined;
    this.viewContextRegistration?.unsubscribe();
    this.interactionController.detach();
    this.zipCodeAreas.detach();
    this.resizeObserver?.disconnect();
    this.mapNavigation.detach(this.map);
    this.map?.remove();
  }

  /**
   * Tell Leaflet its box moved, once per frame at most.
   *
   * A CSS resize can fire as fast as the pointer moves; invalidateSize does
   * real layout work, so notifications are coalesced onto an animation frame.
   */
  private watchOwnSize(): void {
    const container = this.map.getContainer();
    if (typeof ResizeObserver === 'undefined') return;   // jsdom / older browsers

    let queued = 0;
    this.resizeObserver = new ResizeObserver(() => {
      if (queued) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        if (!this.destroyed) {
          // pan defaults to true, and that default is the point.
          //
          // pan:true keeps the same geographic centre when a shell resize moves
          // one edge of the map; pan:false anchors the old top-left instead.
          this.mapNavigation.invalidateSize();
        }
      });
    });
    this.resizeObserver.observe(container);
  }


}

const iconRetinaUrl = 'assets/images/marker-icon-2x.png';
const iconUrl = 'assets/images/marker-icon.png';
const shadowUrl = 'assets/images/marker-shadow.png';
const iconDefault = L.icon({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});
L.Marker.prototype.options.icon = iconDefault;
